/**
 * LinkInvoiceModal — universal "connect an existing invoice to this job".
 *
 * Two ways to attach, for ANY job/quote type:
 *   (A) Attach an existing RFOS invoice — re-points its job_id (works for an
 *       unlinked invoice or one currently on another job = a MOVE).
 *   (B) Link a Xero-created invoice — browses Xero and creates an RFOS row
 *       carrying the Xero id; the 5-min poller then reconciles payments.
 */
import { useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, Box,
  TextField, Stack, Typography, Chip, MenuItem, Select, FormControl, InputLabel,
  CircularProgress,
} from '@mui/material';
import { useInvoices, useUpdateInvoice } from '../../hooks/useInvoices';
import { useXeroStatus, useXeroInvoices, useLinkXeroInvoice } from '../../hooks/useXero';
import { useToast } from '../layout/Toast';
import { fmtC } from '../../lib/utils';

const INV_TYPES = ['full', 'deposit', 'balance', 'extra'];

export default function LinkInvoiceModal({ job, onClose, onLinked }) {
  const { toast } = useToast();
  const [tab, setTab] = useState(0);
  const [invType, setInvType] = useState('full');

  // ── (A) Existing RFOS invoices ──
  const { data: allInvoices = [] } = useInvoices();
  const updateInvoice = useUpdateInvoice();
  const attachable = useMemo(
    () => allInvoices
      .filter((inv) => inv.jobId !== job.id && inv.status !== 'void')
      .sort((a, b) => (a.jobId ? 1 : 0) - (b.jobId ? 1 : 0)), // unlinked first
    [allInvoices, job.id],
  );

  // ── (B) Xero invoices ──
  const { data: xeroStatus } = useXeroStatus();
  const xeroConnected = !!xeroStatus?.connected;
  const [search, setSearch] = useState('');       // live input
  const [submitted, setSubmitted] = useState(''); // only this drives the query
  // Key the query on the SUBMITTED term so typing doesn't refetch the whole Xero
  // invoice list on every keystroke — it only runs on Enter / the search button.
  const { data: xeroInvoices = [], isFetching: xeroLoading } =
    useXeroInvoices(submitted, { enabled: xeroConnected && tab === 1 });
  const linkXero = useLinkXeroInvoice();

  const busy = updateInvoice.isPending || linkXero.isPending;

  const attachRfos = (inv) => {
    if (busy) return;
    // Re-pointing an invoice already on another job is a destructive MOVE
    // (recomputes the source job's ledger) — confirm first.
    if (inv.jobId && !window.confirm(
      `${inv.number || 'This invoice'} is currently on another job. Move it to "${job.title || 'this job'}"? Its earnings will be recomputed on both jobs.`
    )) return;
    updateInvoice.mutate(
      { id: inv.id, jobId: job.id, _prevJobId: inv.jobId || null },
      {
        onSuccess: () => { toast(`Linked ${inv.number || 'invoice'} to job`, 'success'); onLinked?.(); onClose(); },
        onError: (err) => toast(err?.message || 'Failed to link invoice', 'error'),
      },
    );
  };

  const linkXeroInv = (x) => {
    if (busy) return;
    linkXero.mutate(
      { xeroInvoiceId: x.xeroInvoiceId, jobId: job.id, invoiceType: invType },
      {
        onSuccess: () => { toast(`Linked Xero invoice ${x.number || ''} to job`, 'success'); onLinked?.(); onClose(); },
        onError: (err) => toast(err?.message || 'Failed to link Xero invoice', 'error'),
      },
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>Link an invoice to this job</DialogTitle>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ px: 3 }}>
        <Tab label="Existing invoice" />
        <Tab label="From Xero" />
      </Tabs>
      <DialogContent dividers sx={{ minHeight: 320 }}>
        {tab === 0 && (
          <Stack spacing={1}>
            {attachable.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                No other invoices to attach.
              </Typography>
            ) : attachable.map((inv) => (
              <Box
                key={inv.id}
                onClick={() => attachRfos(inv)}
                sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, '&:hover': { borderColor: 'primary.main' } }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{inv.number || 'Draft'}</Typography>
                    <Chip size="small" label={inv.invoiceType || 'full'} variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                    <Chip size="small" label={inv.status} sx={{ height: 18, fontSize: '0.6rem' }} />
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtC(inv.amount)}</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {inv.client || 'No client'}{inv.jobId ? ' · currently on another job — will MOVE' : ' · unlinked'}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}

        {tab === 1 && (
          !xeroConnected ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              Xero isn't connected. Connect it in Settings → Integrations.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small" fullWidth placeholder="Search by number or client — press Enter"
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSubmitted(search.trim()); }}
                />
                <Button size="small" variant="outlined" onClick={() => setSubmitted(search.trim())} sx={{ flexShrink: 0 }}>Search</Button>
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <InputLabel>Type</InputLabel>
                  <Select value={invType} label="Type" onChange={(e) => setInvType(e.target.value)}>
                    {INV_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
              {xeroLoading ? (
                <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
              ) : xeroInvoices.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  No matching unlinked Xero invoices.
                </Typography>
              ) : xeroInvoices.map((x) => (
                <Box
                  key={x.xeroInvoiceId}
                  onClick={() => linkXeroInv(x)}
                  sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, '&:hover': { borderColor: 'primary.main' } }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{x.number || '(no number)'}</Typography>
                      <Chip size="small" label={x.status} sx={{ height: 18, fontSize: '0.6rem' }} />
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtC(x.total)}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {x.contactName || 'No contact'}{x.amountDue > 0 ? ` · ${fmtC(x.amountDue)} due` : ' · paid'}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
